import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../../components/Layout/AppLayout';
import FormProgress from '../../components/ApplicationForm/FormProgress';
import FormField from '../../components/ApplicationForm/FormField';
import FileUploader from '../../components/ApplicationForm/FileUploader';
import LinkField from '../../components/ApplicationForm/LinkField';
import DeclarationRow from '../../components/ApplicationForm/DeclarationRow';
import { useDraftSave } from '../../hooks/useDraftSave';
import { api } from '../../services/api';
import type {
  ApplicationFormResponse,
  FormSectionConfig,
  DeclarationData,
  DeclarationKey,
  UploadType,
  DeclarationConfig,
} from '@vedjegy/shared';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TAGOZAT_OPTIONS = [
  'Szolgáltatási tagozat',
  'Ipari tagozat',
  'Kereskedelmi tagozat',
  'Kézműipari tagozat',
] as const;

export const CONTACT_FIELDS = [
  { key: 'legal_rep',         label: 'Képviseletére jogosult személy' },
  { key: 'form_filler',       label: 'Adatlap kitöltéséért felelős személy' },
  { key: 'trademark_contact', label: 'Védjegyprogram kapcsolattartó' },
] as const;

// Declaration lists are loaded from API (see declarationConfig state in ApplicationFormPage)

export const FORM_FIELD_LABELS: Record<string, string> = {
  chamber_division: 'Kamarai tagozat',
  employee_count:   'Foglalkoztatottak létszáma',
  ...Object.fromEntries(
    CONTACT_FIELDS.flatMap(({ key, label }) => [
      [`${key}_name`,  `${label} — Név`],
      [`${key}_phone`, `${label} — Telefon`],
      [`${key}_email`, `${label} — E-mail`],
    ])
  ),
  // section field keys
  text:          'Szöveg',
  content:       'Tartalom',
  training:      'Képzés',
  motivation:    'Motiváció',
  main:          'Fő tevékenység',
  pride:         'Büszkeség',
  satisfaction:  'Elégedettség',
  developments:  'Fejlesztések',
  results:       'Eredmények',
};

const UPLOAD_TYPES: Array<{ type: UploadType; label: string; accept: string; multiple: boolean; newOnly?: boolean; required: boolean }> = [
  // logo and photo are in the 'publication' wizard step
  // Recommendation is required for new applicants only; renewals skip it
  { type: 'recommendation', label: 'Kamarai tag ajánlása (min. 2 db)',        accept: '.pdf,.doc,.docx',     multiple: true,  newOnly: true,  required: true  },
  { type: 'certificate',    label: 'Minőségirányítási tanúsítvány',          accept: '.pdf',                multiple: false, required: false },
  { type: 'award',          label: 'Díjak, elismerések',                     accept: '.pdf,.jpg,.jpeg,.png',multiple: true,  required: false },
];

// Flat label map for all upload types (used by summary + print export).
// logo and photo labels are added explicitly because they are in the publication step
// and are no longer present in UPLOAD_TYPES.
const UPLOAD_TYPE_LABELS: Record<string, string> = {
  ...Object.fromEntries(UPLOAD_TYPES.map((u) => [u.type, u.label])),
  logo:  'Logó (vektoros, eps/pdf/svg)',
  photo: 'Fényképek (2-3 db, 300 dpi, jpg/png)',
};

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const SECTION_FIELD_MAP: Record<string, { section: string; fieldKey: string }> = {
  'intro':             { section: 'intro',  fieldKey: 'text' },
  '3.1':               { section: '3.1',    fieldKey: 'content' },
  '3.2':               { section: '3.2',    fieldKey: 'content' },
  '3.2_training':      { section: '3.2',    fieldKey: 'training' },
  '3.2_motivation':    { section: '3.2',    fieldKey: 'motivation' },
  '3.3':               { section: '3.3',    fieldKey: 'content' },
  '3.3_main':          { section: '3.3',    fieldKey: 'main' },
  '3.3_pride':         { section: '3.3',    fieldKey: 'pride' },
  '3.3_satisfaction':  { section: '3.3',    fieldKey: 'satisfaction' },
  '3.4':               { section: '3.4',    fieldKey: 'content' },
  '3.5':               { section: '3.5',    fieldKey: 'content' },
  '3.6':               { section: '3.6',    fieldKey: 'content' },
  '3.7':               { section: '3.7',    fieldKey: 'content' },
  '3.8':               { section: '3.8',    fieldKey: 'content' },
  '3.9':               { section: '3.9',    fieldKey: 'content' },
  '3.10':              { section: '3.10',   fieldKey: 'content' },
  '3.10_main':         { section: '3.10',   fieldKey: 'main' },
  '3.10_developments': { section: '3.10',   fieldKey: 'developments' },
  '3.10_results':      { section: '3.10',   fieldKey: 'results' },
};

// ---------------------------------------------------------------------------
// Contact field format validators — used on blur and at submit time
// ---------------------------------------------------------------------------

/** Returns an error string if the phone value is malformed, '' if valid. */
function checkPhoneFormat(v: string): string {
  const digits = v.replace(/\D/g, '');
  if (digits.length < 7)  return 'Érvénytelen telefonszám — legalább 7 számjegy szükséges.';
  if (digits.length > 15) return 'Érvénytelen telefonszám — legfeljebb 15 számjegy engedélyezett.';
  if (!/^[+]?[\d][\d\s\-().]{5,20}$/.test(v)) return 'Érvénytelen telefonszám formátum.';
  return '';
}

/** Returns an error string if the email value is malformed, '' if valid. */
function checkEmailFormat(v: string): string {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return 'Érvénytelen e-mail cím.';
  return '';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// A structured validation error that can navigate the user to the failing step
interface SubmitError {
  stepLabel: string;
  stepIndex: number; // -1 = not navigable (server-side error)
  detail: string;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function ApplicationFormPage() {
  const [form, setForm]         = useState<ApplicationFormResponse | null>(null);
  const [exclusionDecls, setExclusionDecls]   = useState<DeclarationConfig[]>([]);
  const [submissionDecls, setSubmissionDecls] = useState<DeclarationConfig[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [step, setStep]         = useState(0);
  const [searchParams]          = useSearchParams();
  const didJumpToSection        = useRef(false);
  const [submitErrors, setSubmitErrors] = useState<SubmitError[]>([]);
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [liabilityChecked, setLiabilityChecked] = useState(false);

  // Autosave hook — manages dataRef, saveStatus, debounced saveField, flushPendingSave
  const { saveField, flushPendingSave, saveStatus, dataRef } = useDraftSave();

  // In-memory declarations mirror (avoid re-renders on every toggle)
  const declarRef = useRef<Partial<DeclarationData>>({});
  const [dataVersion, setDataVersion] = useState(0);

  // Jump to the section specified by ?section=<sectionId> on first load
  useEffect(() => {
    if (!form || didJumpToSection.current) return;
    const sectionId = searchParams.get('section');
    if (!sectionId) return;
    const builtSteps = buildSteps(form.sectionConfig);
    const idx = builtSteps.findIndex((s) => s.type === 'section' && s.cfg?.sectionId === sectionId);
    if (idx >= 0) setStep(idx);
    didJumpToSection.current = true;
  }, [form, searchParams]);

  useEffect(() => {
    Promise.all([
      api.applicationForm.load(),
      api.config.listDeclarations(),
    ])
      .then(([f, decls]) => {
        setForm(f);
        dataRef.current   = JSON.parse(JSON.stringify(f.data));
        declarRef.current = { ...f.declarations };
        setExclusionDecls(decls.exclusion);
        setSubmissionDecls(decls.submission);
      })
      .catch(() => setError('Nem sikerült betölteni a pályázati adatlapot.'))
      .finally(() => setLoading(false));
  }, []);

  const saveDeclaration = useCallback((key: DeclarationKey | 'liability_accepted_city', value: boolean | string | null) => {
    (declarRef.current as Record<string, unknown>)[key] = value;
    setDataVersion((v) => v + 1);
    void api.applicationForm.saveDeclarations({ [key]: value } as Partial<DeclarationData>);
  }, []);

  const steps = form ? buildSteps(form.sectionConfig) : [];
  const totalSteps = steps.length;

  // Compute per-step completion for the progress bar.
  // - 'declarations': all exclusion decls answered + at least one submission decl answered.
  // - 'section': content meets min/max char constraints.
  // - Everything else (basic, attachments, liability): always false — no reliable heuristic.
  // dataRef / declarRef are refs, so this is always up-to-date at render time without
  // needing extra state; dataVersion bumps ensure re-renders when values change.
  const completedSteps: boolean[] = steps.map((s) => {
    if (s.type === 'basic') {
      const c = dataRef.current['contacts'] ?? {};
      const divisionOk   = !!(c['chamber_division'] ?? '').trim();
      const empOk        = !!(c['employee_count']   ?? '').trim();
      const contactsOk   = CONTACT_FIELDS.every(({ key }) =>
        !!(c[`${key}_name`]  ?? '').trim() &&
        !!(c[`${key}_phone`] ?? '').trim() &&
        !!(c[`${key}_email`] ?? '').trim(),
      );
      return divisionOk && empOk && contactsOk;
    }
    if (s.type === 'declarations') {
      const d = declarRef.current as Record<string, boolean | null | undefined>;
      const allExclusionAnswered = exclusionDecls.length === 0 ||
        exclusionDecls.every((x) => d[x.declarationKey] != null);
      const anySubmissionAnswered = submissionDecls.length === 0 ||
        submissionDecls.some((x) => d[x.declarationKey] != null);
      return allExclusionAnswered && anySubmissionAnswered;
    }
    if (s.type === 'attachments') {
      if (!form) return false;
      // logo and photo moved to the publication step; only recommendation checked here
      const recCount = form.uploads.filter((u) => u.type === 'recommendation').length;
      const recOk = form.applicationType === 'renewal' || recCount >= 2;
      return recOk;
    }
    if (s.type === 'publication') {
      if (!form) return false;
      // Renewal applicants must either choose "use previous" OR have uploaded logo + photo.
      // New applicants must always upload logo + photo.
      // pub_intro has min=0 so it never blocks completion.
      if (form.usePreviousPublication) return true;
      const hasUpload = (t: UploadType) => form.uploads.some((u) => u.type === t);
      return hasUpload('logo') && hasUpload('photo');
    }
    if (s.type !== 'section' || !s.cfg || !form) return false;
    const isOptional = form.applicationType === 'renewal' && !s.cfg.mandatoryForRenewal;
    if (isOptional) return false;
    const mapped = SECTION_FIELD_MAP[s.cfg.sectionId];
    if (!mapped) return false;
    const value = (dataRef.current[mapped.section]?.[mapped.fieldKey] ?? '').trim();
    const min = s.cfg.minChars ?? 1;
    const max = s.cfg.maxChars;
    return value.length >= min && (max == null || value.length <= max);
  });

  // Build a detailed client-side error list before attempting submit.
  // Each error identifies the step by label and index so the user can navigate directly.
  function buildSubmitErrors(): SubmitError[] {
    const errs: SubmitError[] = [];
    if (!form) return errs;

    // Validate required basic-step fields: chamber division, employee count, all contact fields
    const basicStepIndex = steps.findIndex((s) => s.type === 'basic');
    if (basicStepIndex >= 0) {
      const contacts = dataRef.current['contacts'] ?? {};

      if (!(contacts['chamber_division'] ?? '').trim())
        errs.push({ stepLabel: 'Kamarai tagozat', stepIndex: basicStepIndex, detail: 'Kötelező mező.' });
      if (!(contacts['employee_count'] ?? '').trim())
        errs.push({ stepLabel: 'Foglalkoztatottak létszáma', stepIndex: basicStepIndex, detail: 'Kötelező mező.' });

      for (const { key, label } of CONTACT_FIELDS) {
        const name  = (contacts[`${key}_name`]  ?? '').trim();
        const phone = (contacts[`${key}_phone`] ?? '').trim();
        const email = (contacts[`${key}_email`] ?? '').trim();

        if (!name)
          errs.push({ stepLabel: `${label} — Név`, stepIndex: basicStepIndex, detail: 'Kötelező mező.' });

        if (!phone) {
          errs.push({ stepLabel: `${label} — Telefon`, stepIndex: basicStepIndex, detail: 'Kötelező mező.' });
        } else {
          const err = checkPhoneFormat(phone);
          if (err) errs.push({ stepLabel: `${label} — Telefon`, stepIndex: basicStepIndex, detail: err });
        }

        if (!email) {
          errs.push({ stepLabel: `${label} — E-mail`, stepIndex: basicStepIndex, detail: 'Kötelező mező.' });
        } else {
          const err = checkEmailFormat(email);
          if (err) errs.push({ stepLabel: `${label} — E-mail`, stepIndex: basicStepIndex, detail: err });
        }
      }
    }

    // Validate section text fields
    steps.forEach((s, i) => {
      if (s.type !== 'section' || !s.cfg) return;
      const isOptional = form.applicationType === 'renewal' && !s.cfg.mandatoryForRenewal;
      if (isOptional) return;
      const mapped = SECTION_FIELD_MAP[s.cfg.sectionId];
      if (!mapped) return;
      const value = (dataRef.current[mapped.section]?.[mapped.fieldKey] ?? '').trim();
      const min = s.cfg.minChars ?? 1;
      const max = s.cfg.maxChars;
      // Use the full section title (shown as h2 in the step) instead of the short nav label
      const label = s.cfg.title || s.label;
      if (value.length < min) {
        errs.push({ stepLabel: label, stepIndex: i, detail: `Minimum ${min} karakter szükséges (jelenleg: ${value.length})` });
      } else if (max != null && value.length > max) {
        errs.push({ stepLabel: label, stepIndex: i, detail: `Maximum ${max} karakter engedélyezett (jelenleg: ${value.length})` });
      }
    });

    // Validate attachments step: at least 2 recommendation uploads for new applicants
    const attachStepIndex = steps.findIndex((s) => s.type === 'attachments');
    if (attachStepIndex >= 0 && form.applicationType !== 'renewal') {
      const recCount = form.uploads.filter((u) => u.type === 'recommendation').length;
      if (recCount < 2) {
        errs.push({ stepLabel: 'Kamarai tag ajánlása', stepIndex: attachStepIndex, detail: `Legalább 2 ajánlás feltöltése kötelező (jelenleg: ${recCount}).` });
      }
    }

    // Validate publication step:
    // - Renewal applicants: must either have usePreviousPublication=true OR have uploaded logo + photo
    // - New applicants: must always upload logo + photo
    // - pub_intro: min 350, max 1700 chars — validated for all applicants when not using previous publication
    const pubStepIndex = steps.findIndex((s) => s.type === 'publication');
    if (pubStepIndex >= 0) {
      if (form.applicationType === 'renewal' && !form.usePreviousPublication && !form.uploads.some((u) => u.type === 'logo') && !form.uploads.some((u) => u.type === 'photo')) {
        // Neither option chosen yet
        errs.push({ stepLabel: 'Kiadványanyagok', stepIndex: pubStepIndex, detail: 'Válasszon: töltsön fel új anyagokat, vagy kérje a tavalyi adatok felhasználását.' });
      } else if (!form.usePreviousPublication) {
        const pubIntroText = (dataRef.current['pub_intro']?.['text'] ?? '');
        if (pubIntroText.length < 350) {
          errs.push({ stepLabel: 'Kiadványban megjelenő bemutatkozó szöveg', stepIndex: pubStepIndex, detail: `Minimum 350 karakter szükséges (jelenleg: ${pubIntroText.length})` });
        } else if (pubIntroText.length > 1700) {
          errs.push({ stepLabel: 'Kiadványban megjelenő bemutatkozó szöveg', stepIndex: pubStepIndex, detail: `Maximum 1700 karakter engedélyezett (jelenleg: ${pubIntroText.length})` });
        }
        if (!form.uploads.some((u) => u.type === 'logo')) {
          errs.push({ stepLabel: 'Logó', stepIndex: pubStepIndex, detail: 'Kötelező melléklet.' });
        }
        if (!form.uploads.some((u) => u.type === 'photo')) {
          errs.push({ stepLabel: 'Fényképek', stepIndex: pubStepIndex, detail: 'Kötelező melléklet.' });
        }
      }
    }

    return errs;
  }

  if (loading) return <AppLayout><div style={{ padding: 40, color: 'var(--muted-foreground)' }}>Betöltés...</div></AppLayout>;
  if (error || !form) return (
    <AppLayout>
      <div style={{ padding: 40 }}>
        <div className="alert alert-destructive"><span>&#9888;</span><div><p>{error || 'Pályázat nem található.'}</p></div></div>
      </div>
    </AppLayout>
  );

  if (submitted || form.status === 'submitted') {
    return (
      <AppLayout>
        <div style={{ padding: '48px 40px', maxWidth: 2000 }}>
          <div style={{ background: '#e8f8ee', border: '1px solid #4EC07C', borderRadius: 'var(--radius-lg)', padding: '32px 36px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#10003;</div>
            <h2 style={{ margin: '0 0 8px', color: 'var(--primary)', fontSize: 22 }}>Pályázat beküldve!</h2>
            <p style={{ color: 'var(--muted-foreground)', margin: 0 }}>Hamarosan felvesszük Önnel a kapcsolatot a következő lépésekről.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Block access when the application is locked due to an exclusion review
  if (form.status === 'pending_exclusion_review' || form.status === 'excluded') {
    return (
      <AppLayout>
        <div style={{ padding: '48px 40px', maxWidth: 2000 }}>
          <div style={{
            background: form.status === 'excluded' ? '#fef2f2' : '#fffbeb',
            border: '2px solid ' + (form.status === 'excluded' ? '#fca5a5' : '#f59e0b'),
            borderRadius: 'var(--radius-lg)',
            padding: '32px 36px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>
              {form.status === 'excluded' ? '✗' : '⏳'}
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22,
              color: form.status === 'excluded' ? '#b91c1c' : '#92400e',
            }}>
              {form.status === 'excluded' ? 'Pályázat kizárva' : 'Adminisztrátori vizsgálatra vár'}
            </h2>
            <p style={{ color: 'var(--muted-foreground)', margin: 0 }}>
              {form.status === 'excluded'
                ? 'A pályázat kizárásra került. További szerkesztés nem lehetséges.'
                : 'A pályázat adminisztrátori vizsgálat alatt áll. A vizsgálat lezárultáig a szerkesztés szünetel.'}
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const currentStep = steps[step];

  return (
    <AppLayout>
      <div style={{ padding: '28px 40px', maxWidth: 2000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>Pályázati adatlap</h1>
          <span style={{ fontSize: 12, color: saveStatus === 'saving' ? '#b07d00' : saveStatus === 'error' ? '#c0392b' : 'var(--muted-foreground)' }}>
            {saveStatus === 'saving' ? 'Mentés...' : saveStatus === 'saved' ? 'Mentve ✓' : saveStatus === 'error' ? 'Mentési hiba' : ''}
          </span>
        </div>

        <div style={{ marginBottom: 24 }}>
          <FormProgress
            steps={steps.map((s) => s.label)}
            current={step}
            completed={completedSteps}
            onGoTo={(i) => { void flushPendingSave(); setStep(i); }}
          />
        </div>

        <div style={{ maxWidth: 2000, background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '28px 32px', boxShadow: 'var(--shadow-sm)', minHeight: 320 }}>
          {currentStep.type === 'basic'        && <StepBasicData form={form} dataRef={dataRef} saveField={saveField} dataVersion={dataVersion} />}
          {currentStep.type === 'declarations' && <StepDeclarations declarRef={declarRef} saveDeclaration={saveDeclaration} dataVersion={dataVersion} exclusionDecls={exclusionDecls} submissionDecls={submissionDecls} />}
          {currentStep.type === 'section'      && <StepSection key={currentStep.cfg!.sectionId} cfg={currentStep.cfg!} dataRef={dataRef} saveField={saveField} isRenewal={form.applicationType === 'renewal'} form={form} setForm={setForm} />}
          {currentStep.type === 'attachments'  && <StepAttachments form={form} setForm={setForm} />}
          {currentStep.type === 'publication'  && <StepPublication form={form} setForm={setForm} dataRef={dataRef} saveField={saveField} dataVersion={dataVersion} />}
          {currentStep.type === 'summary'      && <StepSummary form={form} dataRef={dataRef} declarRef={declarRef} exclusionDecls={exclusionDecls} submissionDecls={submissionDecls} />}
          {currentStep.type === 'liability'    && (
            <StepLiability
              checked={liabilityChecked}
              setChecked={setLiabilityChecked}
              errors={submitErrors}
              submitting={submitting}
              onGoToStep={(i) => { setSubmitErrors([]); setStep(i); }}
              onSubmit={async () => {
                // Run client-side validation first so the user sees named steps
                const clientErrs = buildSubmitErrors();
                if (clientErrs.length > 0) {
                  setSubmitErrors(clientErrs);
                  return;
                }
                setSubmitting(true);
                setSubmitErrors([]);
                try {
                  await flushPendingSave();
                  await api.applicationForm.submit({});
                  setSubmitted(true);
                } catch (err: unknown) {
                  const e = err as { message?: string; errors?: string[] };
                  // Convert server errors to SubmitError structs (not navigable)
                  const msgs: string[] = e.errors ?? [e.message ?? 'Ismeretlen hiba.'];
                  setSubmitErrors(msgs.map((m) => ({ stepLabel: 'Szerverhiba', stepIndex: -1, detail: m })));
                } finally {
                  setSubmitting(false);
                }
              }}
            />
          )}
        </div>

        {/* Navigation */}
        <div style={{ maxWidth: 2000, display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <button className="btn btn-ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>&#8592; Vissza</button>
          {step < totalSteps - 1 && (
            <button className="btn btn-primary" onClick={() => { void flushPendingSave(); setStep((s) => s + 1); }}>Tovább &#8594;</button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Step builder
// ---------------------------------------------------------------------------

type StepDef = { label: string; type: 'basic' | 'declarations' | 'section' | 'attachments' | 'publication' | 'summary' | 'liability'; cfg?: FormSectionConfig };

function buildSteps(sectionConfig: FormSectionConfig[]): StepDef[] {
  const steps: StepDef[] = [
    { label: 'Alapadatok',    type: 'basic' },
    { label: 'Nyilatkozatok', type: 'declarations' },
  ];
  for (const cfg of sectionConfig) {
    // Use the human-readable title as the label (shown in the tooltip on hover).
    // The sectionId ('3.1' etc.) is an internal key only — never shown to applicants.
    steps.push({ label: cfg.title, type: 'section', cfg });
  }
  steps.push({ label: 'Mellékletek',  type: 'attachments' });
  steps.push({ label: 'Kiadvány',     type: 'publication' });
  steps.push({ label: 'Összesítő',    type: 'summary' });
  steps.push({ label: 'Nyilatkozat',  type: 'liability' });
  return steps;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepBasicData({ form, dataRef, saveField, dataVersion: _dv }: {
  form: ApplicationFormResponse;
  dataRef: React.MutableRefObject<Record<string, Record<string, string>>>;
  saveField: (s: string, k: string, v: string) => void;
  dataVersion: number;
}) {
  const contacts = dataRef.current['contacts'] ?? {};
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  // Bump key to force re-mount (refresh defaultValue) after copy
  const [copyKeys, setCopyKeys] = useState<Record<string, number>>({ form_filler: 0, trademark_contact: 0 });

  // Chamber chapter: auto-populate from company profile; lock field if registry value is present
  const companyChapter = form.companyChapter ?? null;
  useEffect(() => {
    const existing = dataRef.current['contacts']?.['chamber_division'] ?? '';
    if (!existing && companyChapter) {
      if (!dataRef.current['contacts']) dataRef.current['contacts'] = {};
      dataRef.current['contacts']['chamber_division'] = companyChapter;
      saveField('contacts', 'chamber_division', companyChapter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Employee count: controlled input with auto-populate from company profile + mismatch warning
  const companyCount = form.companyEmployeeCount ?? null;
  const [empCount, setEmpCount] = useState<string>(() => {
    const existing = contacts['employee_count'] ?? '';
    // Auto-populate from prequalification if the field is empty
    if (!existing && companyCount != null) return String(companyCount);
    return existing;
  });

  // On first mount, persist the auto-populated value to the DB if the field was empty
  useEffect(() => {
    const existing = dataRef.current['contacts']?.['employee_count'] ?? '';
    if (!existing && companyCount != null) {
      const val = String(companyCount);
      if (!dataRef.current['contacts']) dataRef.current['contacts'] = {};
      dataRef.current['contacts']['employee_count'] = val;
      saveField('contacts', 'employee_count', val);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show a warning when the entered value differs from the prequalification value
  const showEmpWarning =
    companyCount != null && empCount !== '' && Number(empCount) !== companyCount;

  function copyFromLegalRep(targetKey: 'form_filler' | 'trademark_contact') {
    const src = dataRef.current['contacts'] ?? {};
    (['name', 'phone', 'email'] as const).forEach((field) => {
      const val = src[`legal_rep_${field}`] ?? '';
      saveField('contacts', `${targetKey}_${field}`, val);
      // Update dataRef immediately so defaultValue reflects it on re-mount
      if (!dataRef.current['contacts']) dataRef.current['contacts'] = {};
      dataRef.current['contacts'][`${targetKey}_${field}`] = val;
    });
    setCopyKeys((prev) => ({ ...prev, [targetKey]: prev[targetKey] + 1 }));
  }

  function validateContactField(fieldKey: string, field: 'phone' | 'email', value: string) {
    const v = value.trim();
    if (!v) { setContactErrors((prev) => { const n = { ...prev }; delete n[fieldKey]; return n; }); return; }
    const err = field === 'phone' ? checkPhoneFormat(v) : checkEmailFormat(v);
    setContactErrors((prev) => { const n = { ...prev }; if (err) n[fieldKey] = err; else delete n[fieldKey]; return n; });
  }
  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>Alapadatok és kapcsolattartók</h2>
      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 20 }}>
        A vállalkozás alapadatai a profilból kerülnek átvételre. A kapcsolattartók adatai szerkeszthetők.
      </p>

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          Kamarai tagozat <span style={{ color: '#c0392b' }}>*</span>
        </label>
        <select
          value={contacts['chamber_division'] ?? ''}
          onChange={(e) => saveField('contacts', 'chamber_division', e.target.value)}
          disabled={!!companyChapter}
          style={{ width: '100%', marginTop: 6, height: 36, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 14, fontFamily: 'var(--font-sans)', boxSizing: 'border-box', background: companyChapter ? 'var(--muted)' : '#fff', color: companyChapter ? 'var(--muted-foreground)' : 'inherit', cursor: companyChapter ? 'not-allowed' : 'pointer' }}
        >
          <option value="">— Válasszon tagozatot —</option>
          {TAGOZAT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {companyChapter && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
            A tagozatot a kamarai nyilvántartás rögzíti, nem módosítható.
          </p>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          Foglalkoztatottak létszáma az adatlap kitöltésének időpontjában <span style={{ color: '#c0392b' }}>*</span>
        </label>
        <input
          type="number"
          min={0}
          step={1}
          value={empCount}
          onKeyDown={(e) => { if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
          onChange={(e) => {
            const raw = e.target.value;
            // Normalize: empty stays empty, otherwise clamp to non-negative integer
            const normalized = raw === '' ? '' : String(Math.max(0, Math.floor(Number(raw) || 0)));
            setEmpCount(normalized);
            saveField('contacts', 'employee_count', normalized);
          }}
          style={{ width: 140, marginTop: 6, height: 36, padding: '0 10px', border: `1px solid ${showEmpWarning ? '#b45309' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', fontSize: 14, fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
          placeholder="pl. 5"
        />
        {showEmpWarning && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#b45309' }}>
            Eltér az előminősítésben megadott {companyCount} főtől. Szándékos módosítás esetén folytassa.
          </p>
        )}
      </div>

      {CONTACT_FIELDS.map(({ key, label }) => (
        <div key={key} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{label}</div>
            {(key === 'form_filler' || key === 'trademark_contact') && (
              <button
                type="button"
                onClick={() => copyFromLegalRep(key)}
                style={{ fontSize: 11, padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--muted)', color: 'var(--secondary-foreground)', cursor: 'pointer', lineHeight: 1.6 }}
              >
                ← Áttölt a képviselőtől
              </button>
            )}
          </div>
          <div key={copyKeys[key] ?? 0} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {(['name', 'phone', 'email'] as const).map((field) => {
              const fk = `${key}_${field}`;
              const fieldError = contactErrors[fk];
              return (
                <div key={field}>
                  <label style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                    {field === 'name' ? 'Név' : field === 'phone' ? 'Telefon' : 'E-mail'}
                    {' '}<span style={{ color: '#c0392b' }}>*</span>
                  </label>
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    defaultValue={contacts[fk] ?? ''}
                    placeholder={field === 'phone' ? 'pl. +36 96 123 456' : undefined}
                    onChange={(e) => saveField('contacts', fk, e.target.value)}
                    onBlur={(e) => {
                      if (field !== 'name') validateContactField(fk, field, e.target.value);
                    }}
                    style={{ width: '100%', marginTop: 4, height: 34, padding: '0 8px', border: `1px solid ${fieldError ? '#c0392b' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
                  />
                  {fieldError && <div style={{ fontSize: 11, color: '#c0392b', marginTop: 3 }}>{fieldError}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ padding: '12px 16px', background: 'var(--muted)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--muted-foreground)' }}>
        Típus: <strong>{form.formType === 'sole_trader' ? 'Egyéni vállalkozás' : 'Társas vállalkozás'}</strong>
      </div>
    </div>
  );
}

function StepDeclarations({ declarRef, saveDeclaration, dataVersion: _dv, exclusionDecls, submissionDecls }: {
  declarRef: React.MutableRefObject<Partial<DeclarationData>>;
  saveDeclaration: (k: DeclarationKey | 'liability_accepted_city', v: boolean | string | null) => void;
  dataVersion: number;
  exclusionDecls: DeclarationConfig[];
  submissionDecls: DeclarationConfig[];
}) {
  const d = declarRef.current as Record<string, boolean | null>;
  // Exclusion is always triggered by answering Nem (false). Questions are phrased
  // as positive statements, so answering FALSE (nem) is the disqualifying answer.
  const hasExclusion = exclusionDecls.some((x) => x.isExclusionFactor && d[x.declarationKey] === false);

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>Nyilatkozatok</h2>
      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 20 }}>
        Kérjük, az <strong>alábbi táblázat kérdéseire igen/nem</strong> válaszok megadásával nyilatkozzon.<br /><br />
        Alábbi feltételek <u>kizáró tényezőként szerepelhetnek</u>, kérjük pontos kitöltését.<br />
        Külön nyilatkozatot nem kell a pályázathoz benyújtani.
      </p>

      {hasExclusion && (
        <div className="alert alert-destructive" style={{ marginBottom: 16 }}>
          <span>&#9888;</span>
          <div><p>Egy vagy több kizáró tényező fennáll. A pályázat befogadhatóságát az adminisztrátor vizsgálja meg.</p></div>
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Kizáró tényezők</div>
      {exclusionDecls.map((x) => (
        <DeclarationRow key={x.declarationKey} label={x.label} value={d[x.declarationKey] ?? null} onChange={(v) => saveDeclaration(x.declarationKey as DeclarationKey, v)} isExclusion={x.isExclusionFactor} />
      ))}

      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 20, marginBottom: 8 }}>Alávetési nyilatkozatok</div>
      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 12 }}>
        nyilatkozom, hogy a fogyasztóvédelemről szóló 1997. évi CLV. törvény 36/C.§ (1) bekezdése alapján
        a vállalkozásommal szemben lefolytatott békéltető testületi eljárásnak(oknak) alávetem magam
        és egyezség hiányában a<br />
        <em>(csak az egyik lehetőség megjelölése is elegendő)</em>
      </p>
      {submissionDecls.map((x) => (
        <DeclarationRow key={x.declarationKey} label={x.label} value={d[x.declarationKey] ?? null} onChange={(v) => saveDeclaration(x.declarationKey as DeclarationKey, v)} />
      ))}
    </div>
  );
}

// StepSection delegates entirely to FormField — no hooks here, early return is safe
function StepSection({ cfg, dataRef, saveField, isRenewal, form, setForm }: {
  cfg: FormSectionConfig;
  dataRef: React.MutableRefObject<Record<string, Record<string, string>>>;
  saveField: (s: string, k: string, v: string) => void;
  isRenewal?: boolean;
  form: ApplicationFormResponse;
  setForm: React.Dispatch<React.SetStateAction<ApplicationFormResponse | null>>;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const mapped = SECTION_FIELD_MAP[cfg.sectionId];
  if (!mapped) return <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Ismeretlen szekció: {cfg.sectionId}</div>;

  const { section, fieldKey } = mapped;
  const sectionUploads = form.uploads.filter((u) => u.type === 'section_file' && u.sectionId === cfg.sectionId);

  async function handleSectionFiles(files: File[]) {
    setUploading(true);
    setUploadError('');
    try {
      for (const file of files) {
        const { uploadId, uploadUrl, fileKey } = await api.applicationForm.getUploadUrl({
          type: 'section_file', fileName: file.name, mimeType: file.type,
        });
        if (!uploadUrl.startsWith('/api/')) {
          await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        }
        const item = await api.applicationForm.registerUpload({
          uploadId, type: 'section_file', fileKey, fileName: file.name,
          fileSize: file.size, mimeType: file.type, sectionId: cfg.sectionId,
        });
        setForm((f) => f ? { ...f, uploads: [...f.uploads, item] } : f);
      }
    } catch {
      setUploadError('Feltöltés sikertelen. Kérjük, próbálja újra.');
    } finally {
      setUploading(false);
    }
  }

  async function handleSectionDelete(id: string) {
    await api.applicationForm.deleteUpload(id);
    setForm((f) => f ? { ...f, uploads: f.uploads.filter((u) => u.id !== id) } : f);
  }

  return (
    <div>
      <FormField
        label={cfg.title}
        description={cfg.questionText ?? undefined}
        defaultValue={dataRef.current[section]?.[fieldKey] ?? ''}
        onChange={(v) => saveField(section, fieldKey, v)}
        minChars={cfg.minChars}
        maxChars={cfg.maxChars}
        maxScore={cfg.maxScore}
        isOptional={isRenewal && !cfg.mandatoryForRenewal}
      />
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted-foreground)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 8 }}>
          Mellékletek ehhez a szekcióhoz
          <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--muted-foreground)', background: 'var(--muted)', borderRadius: 4, padding: '1px 6px' }}>opcionális</span>
        </div>
        {cfg.suggestedAttachments && (
          <div style={{
            fontSize: 12, color: '#0369a1', background: '#f0f9ff',
            border: '1px solid #bae6fd', borderRadius: 6,
            padding: '7px 12px', marginBottom: 10,
          }}>
            <strong>Javasolt mellékletek:</strong> {cfg.suggestedAttachments}
          </div>
        )}
        {uploadError && (
          <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>{uploadError}</div>
        )}
        <FileUploader
          type="section_file"
          label="Dokumentumok"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls"
          multiple={true}
          items={sectionUploads}
          uploading={uploading}
          onFileSelect={(files) => void handleSectionFiles(files)}
          onDelete={(id) => void handleSectionDelete(id)}
          onDownload={(id) => { const w = window.open('', '_blank'); void api.applicationForm.getUploadDownloadUrl(id).then(({ url }) => { if (w) w.location.href = url; }).catch(() => w?.close()); }}
        />
      </div>
    </div>
  );
}

function StepPublication({ form, setForm, dataRef, saveField, dataVersion: _dv }: {
  form: ApplicationFormResponse;
  setForm: React.Dispatch<React.SetStateAction<ApplicationFormResponse | null>>;
  dataRef: React.MutableRefObject<Record<string, Record<string, string>>>;
  saveField: (s: string, k: string, v: string) => void;
  dataVersion: number;
}) {
  const [uploading, setUploading]     = useState<Record<string, boolean>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [choiceSaving, setChoiceSaving] = useState(false);

  const isRenewal = form.applicationType === 'renewal';
  const usePrev   = form.usePreviousPublication;

  async function handleChoiceChange(usePrevious: boolean) {
    setChoiceSaving(true);
    try {
      await api.applicationForm.savePublicationChoice(usePrevious);
      setForm((f) => f ? { ...f, usePreviousPublication: usePrevious } : f);
    } finally {
      setChoiceSaving(false);
    }
  }

  async function handleFiles(type: UploadType, files: File[]) {
    setUploading((u) => ({ ...u, [type]: true }));
    setUploadErrors((e) => { const n = { ...e }; delete n[type]; return n; });
    try {
      for (const file of files) {
        const { uploadId, uploadUrl, fileKey } = await api.applicationForm.getUploadUrl({
          type, fileName: file.name, mimeType: file.type,
        });
        if (!uploadUrl.startsWith('/api/')) {
          await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        }
        const item = await api.applicationForm.registerUpload({
          uploadId, type, fileKey, fileName: file.name, fileSize: file.size, mimeType: file.type,
        });
        setForm((f) => f ? { ...f, uploads: [...f.uploads, item] } : f);
      }
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Feltöltés sikertelen. Kérjük, próbálja újra.';
      setUploadErrors((e) => ({ ...e, [type]: msg }));
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  }

  async function handleDelete(id: string) {
    await api.applicationForm.deleteUpload(id);
    setForm((f) => f ? { ...f, uploads: f.uploads.filter((u) => u.id !== id) } : f);
  }

  const PUBLICATION_UPLOAD_TYPES = [
    { type: 'logo'  as UploadType, label: 'Logó (vektoros, eps/pdf/svg)',         accept: '.eps,.pdf,.svg,.ai',  multiple: false, maxFiles: undefined, required: true },
    { type: 'photo' as UploadType, label: 'Fényképek (2-3 db, 300 dpi, jpg/png)', accept: '.jpg,.jpeg,.png',     multiple: true,  maxFiles: 3,         required: true },
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>Kiadványhoz szükséges anyagok</h2>
      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 20 }}>
        Az alábbi anyagok sikeres pályázat esetén a Védjegy kiadványban jelennek meg.
      </p>

      {/* Renewal applicants: single checkbox to reuse last year's publication materials */}
      {isRenewal && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 24, cursor: choiceSaving ? 'not-allowed' : 'pointer', opacity: choiceSaving ? 0.6 : 1 }}>
          <input
            type="checkbox"
            checked={usePrev}
            disabled={choiceSaving}
            onChange={(e) => void handleChoiceChange(e.target.checked)}
            style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
          />
          <span style={{ fontSize: 13, color: 'var(--primary)' }}>
            A tavalyi kiadványban megjelent bemutatkozó szöveget változtatás nélkül szeretném a következő évi kiadványban felhasználni.
          </span>
        </label>
      )}

      {/* Show upload fields only when not reusing previous year's materials */}
      {!usePrev && (
        <>
          {/* Instructional text with structure */}
          <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 16, lineHeight: 1.6, padding: '12px 16px', background: 'var(--muted)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
            <p style={{ margin: '0 0 8px' }}>Kérjük mutassa be vállalkozását a kiadvány olvasói számára és ismertesse azokat a szolgáltatásokat, melyeket a vállalkozása nyújt a partnerei számára.</p>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Javasolt szerkezet:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Rövid szlogen/üzenet (30-90 karakter)</li>
              <li>Bevezető/lead (180-280 karakter)</li>
              <li>Részletes cégbemutató (550-1190 karakter)</li>
              <li>Elérhetőség (60-140 karakter)
                <ul>
                  <li>Név, titulus</li>
                  <li>Weboldal cím</li>
                  <li>Email cím</li>
                  <li>Cím</li>
                  <li>Telefonszám</li>
                </ul>
              </li>
            </ul>
          </div>

          <FormField
            label="Kiadványban megjelenő bemutatkozó szöveg"
            defaultValue={dataRef.current['pub_intro']?.['text'] ?? ''}
            onChange={(v) => saveField('pub_intro', 'text', v)}
            minChars={350}
            maxChars={1700}
          />

          <div style={{ marginTop: 24 }}>
            {PUBLICATION_UPLOAD_TYPES.map(({ type, label, accept, multiple, maxFiles }) => (
              <div key={type}>
                <FileUploader
                  type={type}
                  label={label}
                  accept={accept}
                  multiple={multiple}
                  maxFiles={maxFiles}
                  items={form.uploads.filter((u) => u.type === type)}
                  uploading={uploading[type] ?? false}
                  onFileSelect={(files) => void handleFiles(type, files)}
                  onDelete={(id) => void handleDelete(id)}
                  onDownload={(id) => { const w = window.open('', '_blank'); void api.applicationForm.getUploadDownloadUrl(id).then(({ url }) => { if (w) w.location.href = url; }).catch(() => w?.close()); }}
                />
                {uploadErrors[type] && (
                  <div className="alert alert-destructive" style={{ marginTop: 6, marginBottom: 8 }}>
                    <span>&#9888;</span><div><p>{uploadErrors[type]}</p></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {usePrev && (
        <div style={{ padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, color: '#166534' }}>
          A tavalyi kiadvány adatok felhasználását kérte. Feltöltés nem szükséges.
        </div>
      )}
    </div>
  );
}

function StepAttachments({ form, setForm }: {
  form: ApplicationFormResponse;
  setForm: React.Dispatch<React.SetStateAction<ApplicationFormResponse | null>>;
}) {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [uploadError, setUploadError] = useState('');

  async function handleFiles(type: UploadType, files: File[]) {
    setUploading((u) => ({ ...u, [type]: true }));
    setUploadError('');
    try {
      for (const file of files) {
        const { uploadId, uploadUrl, fileKey } = await api.applicationForm.getUploadUrl({
          type, fileName: file.name, mimeType: file.type,
        });
        if (!uploadUrl.startsWith('/api/')) {
          await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        }
        const item = await api.applicationForm.registerUpload({
          uploadId, type, fileKey, fileName: file.name, fileSize: file.size, mimeType: file.type,
        });
        setForm((f) => f ? { ...f, uploads: [...f.uploads, item] } : f);
      }
    } catch {
      setUploadError('Feltöltés sikertelen. Kérjük, próbálja újra.');
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  }

  async function handleDelete(id: string) {
    await api.applicationForm.deleteUpload(id);
    setForm((f) => f ? { ...f, uploads: f.uploads.filter((u) => u.id !== id) } : f);
  }

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>Mellékletek</h2>

      {uploadError && (
        <div className="alert alert-destructive" style={{ marginBottom: 16 }}>
          <span>&#9888;</span><div><p>{uploadError}</p></div>
        </div>
      )}

      {UPLOAD_TYPES
        .filter((ut) => !ut.newOnly || form.applicationType === 'new')
        .map(({ type, label, accept, multiple, required }) => (
          <FileUploader
            key={type}
            type={type}
            label={required ? label : `${label} (nem kötelező)`}
            accept={accept}
            multiple={multiple}
            items={form.uploads.filter((u) => u.type === type)}
            uploading={uploading[type] ?? false}
            onFileSelect={(files) => void handleFiles(type, files)}
            onDelete={(id) => void handleDelete(id)}
            onDownload={(id) => { const w = window.open('', '_blank'); void api.applicationForm.getUploadDownloadUrl(id).then(({ url }) => { if (w) w.location.href = url; }).catch(() => w?.close()); }}
          />
        ))}

      <LinkField
        label="Videó link – max 1,5 perc (nem kötelező)"
        initialValue={form.data['media']?.['video_url'] ?? ''}
        onChange={(v) => void api.applicationForm.saveField({ section: 'media', fieldKey: 'video_url', value: v })}
        placeholder="https://youtube.com/watch?v=... vagy facebook.com/reel/... stb."
        urlType="any"
        onCheckReachability={(url) => api.applicationForm.checkUrl(url).then((r) => r.reachable)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepSummary helpers
// ---------------------------------------------------------------------------

function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: 'var(--primary)', borderBottom: '2px solid var(--border)', paddingBottom: 6 }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', minWidth: 220, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: (value ?? '').trim() ? 'inherit' : 'var(--muted-foreground)', wordBreak: 'break-all', overflowWrap: 'break-word', minWidth: 0 }}>
        {(value ?? '').trim() || '—'}
      </div>
    </div>
  );
}

function SummaryText({ value }: { value: string }) {
  return (
    <div style={{ fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap', background: 'var(--muted)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>
      {value}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepSummary — read-only application overview with browser print-to-PDF
// ---------------------------------------------------------------------------

function StepSummary({
  form,
  dataRef,
  declarRef,
  exclusionDecls,
  submissionDecls,
}: {
  form: ApplicationFormResponse;
  dataRef: React.MutableRefObject<Record<string, Record<string, string>>>;
  declarRef: React.MutableRefObject<Partial<DeclarationData>>;
  exclusionDecls: DeclarationConfig[];
  submissionDecls: DeclarationConfig[];
}) {
  const contacts = dataRef.current['contacts'] ?? {};
  const decls = declarRef.current as Record<string, boolean | null | undefined>;
  const allDecls = [...exclusionDecls, ...submissionDecls];

  // Build a printable HTML document and open it in a new tab for browser print-to-PDF
  function handlePrintPDF() {
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');

    const sections: string[] = [];

    // Basic data
    let basicRows = '';
    basicRows += `<tr><td class="lbl">Kamarai tagozat</td><td>${esc(contacts['chamber_division'] ?? '—')}</td></tr>`;
    basicRows += `<tr><td class="lbl">Foglalkoztatottak létszáma</td><td>${contacts['employee_count'] ? `${esc(contacts['employee_count'])} fő` : '—'}</td></tr>`;
    for (const { key, label } of CONTACT_FIELDS) {
      const name  = (contacts[`${key}_name`]  ?? '').trim();
      const phone = (contacts[`${key}_phone`] ?? '').trim();
      const email = (contacts[`${key}_email`] ?? '').trim();
      if (name || phone || email) {
        const parts = [name, phone, email].filter(Boolean).map(esc).join(' · ');
        basicRows += `<tr><td class="lbl">${esc(label)}</td><td>${parts}</td></tr>`;
      }
    }
    sections.push(`<section><h2>Alapadatok és kapcsolattartók</h2><table class="dt">${basicRows}</table></section>`);

    // Content sections (sorted by displayOrder)
    for (const cfg of [...form.sectionConfig].sort((a, b) => a.displayOrder - b.displayOrder)) {
      const sd = dataRef.current[cfg.sectionId] ?? {};
      const parts: string[] = [];
      const content = (sd['content'] ?? '').trim();
      if (content) parts.push(`<div class="tb">${esc(content)}</div>`);
      for (const [fk, fv] of Object.entries(sd)) {
        if (fk === 'content' || !(fv ?? '').trim()) continue;
        const fl = FORM_FIELD_LABELS[fk] ?? fk;
        parts.push(`<div style="margin-top:6px"><strong>${esc(fl)}:</strong><div class="tb">${esc(fv)}</div></div>`);
      }
      if (parts.length) {
        sections.push(`<section><h2>${esc(cfg.title)}</h2>${parts.join('')}</section>`);
      }
    }

    // Publication intro text (separate from application intro)
    const pubIntroText = (dataRef.current['pub_intro']?.['text'] ?? '').trim();
    if (pubIntroText) {
      sections.push(`<section><h2>Kiadványban megjelenő bemutatkozó szöveg</h2><div class="tb">${esc(pubIntroText)}</div></section>`);
    }

    // Attachments + video link
    const videoUrl = (dataRef.current['media']?.['video_url'] ?? '').trim();
    if (form.uploads.length || videoUrl) {
      let rows = '';
      for (const u of form.uploads) {
        const tl = UPLOAD_TYPE_LABELS[u.type] ?? u.type;
        const sz = u.fileSize ? ` (${formatFileSize(u.fileSize)})` : '';
        rows += `<tr><td class="lbl">${esc(tl)}</td><td>${esc(u.fileName)}${sz}</td></tr>`;
      }
      if (videoUrl) {
        rows += `<tr><td class="lbl">Videó link</td><td><a href="${esc(videoUrl)}">${esc(videoUrl)}</a></td></tr>`;
      }
      sections.push(`<section><h2>Mellékletek</h2><table class="dt">${rows}</table></section>`);
    }

    // Declarations
    if (allDecls.length) {
      let rows = '';
      for (const d of allDecls) {
        const val = decls[d.declarationKey];
        const display = val === null || val === undefined ? '—' : val ? 'Igen' : 'Nem';
        rows += `<tr><td style="width:80%">${esc(d.label)}</td><td style="font-weight:700">${display}</td></tr>`;
      }
      sections.push(`<section><h2>Nyilatkozatok</h2><table class="dt">${rows}</table></section>`);
    }

    const yearLabel = `${new Date().getFullYear()} · ${form.applicationType === 'renewal' ? 'Megújítói pályázat' : 'Új pályázat'}`;
    const html = `<!DOCTYPE html>
<html lang="hu"><head>
<meta charset="UTF-8">
<title>Pályázati összesítő</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:11pt;color:#111;padding:18mm 20mm}
  h1{font-size:17pt;color:#1a3a5c;margin-bottom:3mm}
  .meta{font-size:10pt;color:#555;margin-bottom:10mm}
  section{margin-bottom:9mm;page-break-inside:avoid}
  h2{font-size:12pt;color:#1a3a5c;border-bottom:1.5px solid #1a3a5c;padding-bottom:1.5mm;margin-bottom:3mm}
  .dt{width:100%;border-collapse:collapse;font-size:10pt}
  .dt tr{border-bottom:1px solid #e5e7eb}
  .dt td{padding:2mm 2.5mm;vertical-align:top}
  .lbl{font-weight:700;color:#374151;width:34%}
  .tb{white-space:pre-wrap;line-height:1.55;padding:2mm 0;font-size:10pt}
  @media print{body{padding:0}}
</style>
</head><body>
<h1>Pályázati összesítő</h1>
<div class="meta">${yearLabel}</div>
${sections.join('\n')}
</body></html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
    }
  }

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>Pályázati összesítő</h2>
        <button
          type="button"
          onClick={handlePrintPDF}
          style={{ padding: '8px 18px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          ⬇ Letöltés / nyomtatás PDF-ben
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 24 }}>
        Ellenőrizze a pályázati anyagot beküldés előtt. Az adatok szerkesztéséhez lépjen vissza a megfelelő lépésre.
      </p>

      {/* Basic data */}
      <SummarySection title="Alapadatok és kapcsolattartók">
        <SummaryRow label="Kamarai tagozat" value={contacts['chamber_division']} />
        <SummaryRow label="Foglalkoztatottak létszáma" value={contacts['employee_count'] ? `${contacts['employee_count']} fő` : ''} />
        {CONTACT_FIELDS.map(({ key, label }) => {
          const name  = (contacts[`${key}_name`]  ?? '').trim();
          const phone = (contacts[`${key}_phone`] ?? '').trim();
          const email = (contacts[`${key}_email`] ?? '').trim();
          if (!name && !phone && !email) return null;
          return (
            <SummaryRow key={key} label={label} value={[name, phone, email].filter(Boolean).join(' · ')} />
          );
        })}
      </SummarySection>

      {/* Content sections */}
      {[...form.sectionConfig]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((cfg) => {
          const sd = dataRef.current[cfg.sectionId] ?? {};
          const hasContent = Object.values(sd).some((v) => (v ?? '').trim());
          if (!hasContent) return null;
          return (
            <SummarySection key={cfg.sectionId} title={cfg.title}>
              {(sd['content'] ?? '').trim() && <SummaryText value={sd['content']} />}
              {Object.entries(sd)
                .filter(([fk, fv]) => fk !== 'content' && (fv ?? '').trim())
                .map(([fk, fv]) => (
                  <div key={fk} style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                      {FORM_FIELD_LABELS[fk] ?? fk}
                    </div>
                    <SummaryText value={fv} />
                  </div>
                ))}
            </SummarySection>
          );
        })}

      {/* Publication intro */}
      {(dataRef.current['pub_intro']?.['text'] ?? '').trim() && (
        <SummarySection title="Kiadványban megjelenő bemutatkozó szöveg">
          <SummaryText value={dataRef.current['pub_intro']['text']} />
        </SummarySection>
      )}

      {/* Attachments + video link */}
      {(form.uploads.length > 0 || (form.data['media']?.['video_url'] ?? '').trim()) && (
        <SummarySection title="Mellékletek">
          {form.uploads.map((u) => (
            <SummaryRow
              key={u.id}
              label={UPLOAD_TYPE_LABELS[u.type] ?? u.type}
              value={`${u.fileName}${u.fileSize ? ` · ${formatFileSize(u.fileSize)}` : ''}`}
            />
          ))}
          {(form.data['media']?.['video_url'] ?? '').trim() && (
            <SummaryRow label="Videó link" value={form.data['media']['video_url']} />
          )}
        </SummarySection>
      )}

      {/* Declarations */}
      {allDecls.length > 0 && (
        <SummarySection title="Nyilatkozatok">
          {allDecls.map((d) => {
            const val = decls[d.declarationKey];
            const display =
              val === null || val === undefined
                ? <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                : <span style={{ fontWeight: 700 }}>{val ? 'Igen' : 'Nem'}</span>;
            return (
              <div key={d.declarationKey} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>{d.label}</div>
                <div style={{ fontSize: 13, minWidth: 36, textAlign: 'right' }}>{display}</div>
              </div>
            );
          })}
        </SummarySection>
      )}
    </div>
  );
}

function StepLiability({ checked, setChecked, errors, submitting, onSubmit, onGoToStep }: {
  checked: boolean;
  setChecked: (v: boolean) => void;
  errors: SubmitError[];
  submitting: boolean;
  onSubmit: () => void;
  onGoToStep: (stepIndex: number) => void;
}) {
  return (
    <div>
      <h2 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700, color: 'var(--primary)' }}>Felelősségvállaló nyilatkozat</h2>
      <div style={{ fontSize: 13, color: 'var(--primary)', lineHeight: 1.7, marginBottom: 20, padding: '16px 20px', background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}>
        Alulírott, mint pályázó / a pályázó képviselője nyilatkozom, hogy a pályázatban közölt adatok a valóságnak megfelelnek, hozzájárulunk a pályázat kiírójának, vagy hivatalos képviselőjének személyes ellenőrzéséhez a pályázat tartalmát illetően.
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 24, fontSize: 13, color: 'var(--primary)' }}>
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ marginTop: 2, flexShrink: 0, width: 'auto' }} />
        <span>A fenti nyilatkozatot megértettem és elfogadom. A pályázatban szereplő adatok a valóságnak megfelelnek.</span>
      </label>

      {errors.length > 0 && (
        <div className="alert alert-destructive" style={{ marginBottom: 16 }}>
          <span>&#9888;</span>
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', marginBottom: 8 }}>
              A pályázat nem küldhető be — {errors.length} hiányos fejezet:
            </strong>
            <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {errors.map((e, i) => (
                <li key={i} style={{ fontSize: 13 }}>
                  {e.stepIndex >= 0 ? (
                    <button
                      onClick={() => onGoToStep(e.stepIndex)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: 'var(--destructive)', fontWeight: 700, fontSize: 13,
                        textDecoration: 'underline', textUnderlineOffset: 2,
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      {e.stepLabel}
                    </button>
                  ) : (
                    <strong>{e.stepLabel}</strong>
                  )}
                  {' — '}
                  <span style={{ color: 'inherit', opacity: 0.85 }}>{e.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <button
        className="btn btn-primary"
        disabled={submitting || !checked}
        onClick={onSubmit}
        style={{ width: '100%', height: 44, fontSize: 15, fontWeight: 700 }}
      >
        {submitting ? 'Beküldés...' : 'Pályázat beküldése'}
      </button>
    </div>
  );
}
